import axios from "axios";
import { buildApiUrl } from "../config/api";
import {
  listOutboxEntries,
  removeOutboxEntry,
  markOutboxRetry,
} from "./outbox";
import { isBrowserOnline } from "./network";
import { OFFLINE_SUBMISSION_PREFIX } from "./teacherApi";

const MAX_RETRIES = 8;
const SYNC_TAG = "teacher-outbox-sync";

function apiUrl(path) {
  return buildApiUrl(path.startsWith("/") ? path : `/${path}`);
}

async function executeEntry(entry) {
  const { type, payload } = entry;

  if (type === "attendance_save") {
    const res = await axios.post(apiUrl("/AttendanceSubmission"), payload);
    return res.data;
  }

  if (type === "attendance_submit") {
    let subId = payload.submissionId;
    if (!subId || String(subId).startsWith(OFFLINE_SUBMISSION_PREFIX)) {
      const saveRes = await axios.post(apiUrl("/AttendanceSubmission"), payload.payload);
      subId = saveRes.data?.id || saveRes.data?._id;
    }
    const res = await axios.put(apiUrl(`/AttendanceSubmission/${subId}/submit`));
    return res.data;
  }

  if (type === "marks_save") {
    const res = await axios.post(apiUrl("/MarksSubmission"), payload);
    return res.data;
  }

  if (type === "marks_submit") {
    let subId = payload.submissionId;
    if (!subId || String(subId).startsWith(OFFLINE_SUBMISSION_PREFIX)) {
      const saveRes = await axios.post(apiUrl("/MarksSubmission"), payload.payload);
      subId = saveRes.data?.id || saveRes.data?._id;
    }
    const res = await axios.put(apiUrl(`/MarksSubmission/${subId}/submit`));
    return res.data;
  }

  throw new Error(`Unknown outbox type: ${type}`);
}

/**
 * Upload all pending teacher mutations. Called when online or via Background Sync.
 * @returns {{ processed: number, failed: number }}
 */
export async function processOutbox() {
  if (!isBrowserOnline()) {
    return { processed: 0, failed: 0, skipped: true };
  }

  const entries = await listOutboxEntries();
  let processed = 0;
  let failed = 0;

  for (const entry of entries) {
    if ((entry.retries || 0) >= MAX_RETRIES) {
      failed += 1;
      continue;
    }
    try {
      await executeEntry(entry);
      await removeOutboxEntry(entry.id);
      processed += 1;
    } catch (err) {
      await markOutboxRetry(entry.id, err?.message || "Sync failed");
      failed += 1;
    }
  }

  return { processed, failed, skipped: false };
}

/** Register Background Sync when supported (Chrome/Android). */
export async function registerBackgroundSync() {
  if (!("serviceWorker" in navigator) || !("SyncManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.sync.register(SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

export const OUTBOX_SYNC_TAG = SYNC_TAG;
